import { useState, useEffect } from "react";

// next.js components
import Link from "next/link";
import { useRouter } from "next/router";

// @material-tailwind/react components
import {
  Navbar as MTNavbar,
  Collapse,
  IconButton,
  Tooltip,
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
  Button,
} from "@material-tailwind/react";
import {
  UserCircleIcon,
  ClipboardIcon,
  Square3Stack3DIcon as BlocksIcon,
} from "@heroicons/react/24/solid";
import { useClerk } from "@clerk/clerk-react";

function formatNumber(number, decPlaces) {
  decPlaces = Math.pow(10, decPlaces);

  const abbrev = ["K", "M", "B", "T"];

  for (let i = abbrev.length - 1; i >= 0; i--) {
    var size = Math.pow(10, (i + 1) * 3);

    if (size <= number) {
      number = Math.round((number * decPlaces) / size) / decPlaces;

      if (number == 1000 && i < abbrev.length - 1) {
        number = 1;
        i++;
      }

      number += abbrev[i];

      break;
    }
  }

  return number;
}

interface NavbarProps {
  container?: string;
  className?: string;
  shadow?: boolean;
  sidenavMenu?: any;
  mobileNavClassName?: string;
  [key: string]: any;
}

export default function Navbar({
  container,
  className,
  shadow,
  sidenavMenu,
  mobileNavClassName = "text-[#1A237E]",
  ...rest
}: NavbarProps) {
  const { openSignIn, openSignUp } = useClerk();
  const { query } = useRouter();
  const [open, setOpen] = useState(false);
  const [stars, setStars] = useState(0);
  const navbarItemClasses =
    "flex items-center px-1 py-2 font-normal transition-all duration-250 text-size-sm text-current font-light lg:px-2 cursor-pointer";

  useEffect(() => {
    window.addEventListener("resize", () => {
      window.innerWidth >= 960 && setOpen(false);
    });
  }, []);

  useEffect(() => {
    const stars = fetch(
      "https://api.github.com/repos/creativetimofficial/material-tailwind",
    )
      .then((response) => response.json())
      .then((data) => setStars(formatNumber(data.stargazers_count, 1)));
  }, []);

  const menuOpenIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 6h16M4 12h16M4 18h16"
      />
    </svg>
  );

  const menuCloseIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      className="h-6 w-6"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );

  const navbarMenu = (
    <div className="flex w-full flex-col lg:flex-row">
      <ul
        className={`${
          open ? "mt-4" : ""
        } mb-0 flex list-none flex-col gap-2 pl-0 text-inherit transition-all lg:ml-auto lg:flex-row lg:gap-4`}
      >
        <Menu placement="bottom" offset={-2.5}>
          <MenuHandler>
            <li>
              <span className={navbarItemClasses}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="mr-1.5 h-[18px] w-[18px] opacity-75"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0016.5 9h-1.875a1.875 1.875 0 01-1.875-1.875V5.25A3.75 3.75 0 009 1.5H5.625zM7.5 15a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5A.75.75 0 017.5 15zm.75 2.25a.75.75 0 000 1.5H12a.75.75 0 000-1.5H8.25z"
                    clipRule="evenodd"
                  />
                  <path d="M12.971 1.816A5.23 5.23 0 0114.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 013.434 1.279 9.768 9.768 0 00-6.963-6.963z" />
                </svg>
                <span>Docs</span>
              </span>
            </li>
          </MenuHandler>
          <MenuList>
            <MenuItem className="p-0">
              <Link
                href="/docs/html/installation"
                className={`${navbarItemClasses} lg:px-3`}
              >
                HTML
              </Link>
            </MenuItem>
            <MenuItem className="p-0">
              <Link
                href="/test"
                className={`${navbarItemClasses} px-3 py-2 lg:px-3`}
              >
                ReactJS
              </Link>
            </MenuItem>
          </MenuList>
        </Menu>
        <li>
          <Link
            href="/blocks"
            className={`${navbarItemClasses} px-3 py-2 lg:px-3`}
          >
            <BlocksIcon className="mr-1.5 h-[18px] w-[18px] opacity-75" />
            Course Structure
          </Link>
        </li>
        <li>
          <a
            href="/compiler"
            target="_blank"
            rel="noreferrer"
            className={`${navbarItemClasses} px-3 py-2 lg:px-3`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="mr-1.5 h-[18px] w-[18px] text-inherit opacity-75"
            >
              <path d="M5.566 4.657A4.505 4.505 0 016.75 4.5h10.5c.41 0 .806.055 1.183.157A3 3 0 0015.75 3h-7.5a3 3 0 00-2.684 1.657zM2.25 12a3 3 0 013-3h13.5a3 3 0 013 3v6a3 3 0 01-3 3H5.25a3 3 0 01-3-3v-6zM5.25 7.5c-.41 0-.806.055-1.184.157A3 3 0 016.75 6h10.5a3 3 0 012.683 1.657A4.505 4.505 0 0018.75 7.5H5.25z" />
            </svg>
            Compiler
          </a>
        </li>
        <li>
          <a
            href="https://forms.office.com/r/hdbAKihbvQ"
            target="_blank"
            rel="noreferrer"
            className={`${navbarItemClasses} px-3 py-2 lg:px-3`}
          >
            <img
              src="https://img.freepik.com/free-vector/job-interview-vector_23-2147499572.jpg?w=1060&t=st=1695322604~exp=1695323204~hmac=7b5a99f372421b8b35b85efbd3d1e4ec00bc72315f09b903c8d3754d9c1ef99a" 
              alt="Image Description" 
              className="mr-1.5 h-[18px] w-[18px] text-inherit opacity-75"
            />
            Internship
          </a>
        </li>
      </ul>
      <div className="mt-4 flex flex-col border-t border-blue-gray-50 pt-4 lg:mt-0 lg:ml-auto lg:flex-row lg:border-0 lg:pt-0">
        <ul className="flex items-center">
          <Tooltip
            content="Join Our Whatsapp Group"
            placement="bottom"
            offset={-2.5}
          >
            <li className="mr-2">
              <a
                className={navbarItemClasses}
                href="https://wa.me/message/BUTAOEAIJMNHO1"
                target="_blank"
                rel="noreferrer"
              >
                <i className="fab fa-whatsapp text-xl leading-none" />
              </a>
            </li>
          </Tooltip>
          <Tooltip
            content="Join our community"
            placement="bottom"
            offset={-2.5}
          >
            <li className="mt-[3px]">
              <a
                className={navbarItemClasses}
                href="https://discord.gg/QBk27gYm"
                target="_blank"
                rel="noreferrer"
              >
                <i className="fab fa-discord text-lg leading-none" />
              </a>
            </li>
          </Tooltip>
        </ul>

        <Button
          onClick={() => openSignIn()}
          variant="gradient"
          className="mt-3 mb-6 flex w-full items-center justify-center lg:mt-0 lg:mb-0 lg:ml-4 lg:w-auto"
        >
          <UserCircleIcon className="mr-1.5 -mt-px h-[18px] w-[18px]" />
          Log In
        </Button>

        <Button
          onClick={() => openSignUp()}
          variant="gradient"
          className="mt-3 mb-6 flex w-full items-center justify-center lg:mt-0 lg:mb-0 lg:ml-4 lg:w-auto"
        >
          <ClipboardIcon className="mr-1.5 -mt-px h-[18px] w-[18px]" />
          Sign Up
        </Button>
      </div>
    </div>
  );

  return (
    <div
      className={`absolute left-2/4 z-[999] my-4 flex w-full max-w-screen-2xl -translate-x-2/4 flex-wrap items-center px-4 lg:fixed ${container}`}
    >
      <MTNavbar
        {...rest}
        className={`py-4 pl-6 pr-2 lg:py-2.5 ${
          shadow ? "shadow-2xl shadow-blue-gray-500/10" : ""
        }`}
        shadow={shadow}
      >
        <div
          className={`flex w-full items-center !justify-between text-[#1A237E] ${className}`}
        >
          <Link
            href="/"
            className="py-2.375 text-size-sm mr-4 whitespace-nowrap font-bold text-inherit lg:ml-0"
          >
            Code Crush
          </Link>
          <IconButton
            variant="text"
            className="ml-auto h-6 w-6 text-inherit hover:bg-transparent focus:bg-transparent active:bg-transparent lg:hidden"
            ripple={false}
            onClick={() => setOpen(!open)}
          >
            {open ? menuCloseIcon : menuOpenIcon}
          </IconButton>
          <div className="lg:base-auto hidden flex-grow basis-full items-center overflow-hidden lg:flex lg-max:max-h-0">
            {navbarMenu}
          </div>
        </div>

        <Collapse open={open} className={mobileNavClassName}>
          {navbarMenu}
        </Collapse>
        {sidenavMenu}
      </MTNavbar>
    </div>
  );
}
